import type {
  CapacitySeed,
  CapacityPlantState,
  CapacityStrategyId,
  CapacityStrategyRead,
  MetalPriceState,
  OreGradeState,
  RecoveryState,
  StrategyLeverRead,
  CapacityStrategyMatrix,
  CapacityStrategyOutcome,
  CapacityRecoveryProjection,
} from '@/versions/v1/schema/capacity';
import { calculateEbitda, capacityOperatingFeed, cappedRecoveriesAtThroughput } from './CapacityEngine';
import { CAPACITY_SEED, DEFAULT_CAPACITY_PLANT_STATE, STRATEGY_LEVERS, STRATEGY_PRICE_LIMITS } from './capacity-seed';
import { BUNKER_HILL_SMELTER_SCHEDULE } from './bunker-hill-smelter-terms';

const RECOVERY_KEYS = ['znPct', 'pbPct', 'agPct'] as const;
const cents = (amount: number) => Math.round(amount * 100) / 100;

/**
 * D23 (third addendum): the cap binds the recovery curve, not a strategy's
 * named-regime lever uplift. A strategy recovery is the capped curve at its feed
 * plus its uplift, bounded only to the physical 0–100 % range — never re-capped.
 * A strategy above its metal's design is that regime's setpoint.
 */
function boundedRecovery(recoveries: RecoveryState): RecoveryState {
  return Object.fromEntries(RECOVERY_KEYS.map((key) => [
    key,
    Math.min(100, Math.max(0, recoveries[key])),
  ])) as RecoveryState;
}

/** Matrix contract for the locked comp; raw engine lever impacts stay available. */
function strategyMatrix(
  strategies: readonly CapacityStrategyOutcome[],
  currentEbitdaA: number,
  currentFeed: number,
  currentRecoveries: RecoveryState,
  plantState: CapacityPlantState,
  daysPerYear: number,
): CapacityStrategyMatrix {
  const balanced = strategies.find(row => row.id === 'balanced')!;
  const columns: CapacityStrategyMatrix['columns'] = [
    { id: 'current', name: 'Current', feedStph: currentFeed, recoveries: currentRecoveries,
      ebitdaA: currentEbitdaA, upliftA: 0, annualisedUpliftA: 0,
      netVsBalancedA: cents(currentEbitdaA - balanced.financials.ebitdaA) },
    ...strategies.map(row => {
      const upliftA = cents(row.financials.ebitdaA - currentEbitdaA);
      return { id: row.id, name: row.name, feedStph: row.feedStph, recoveries: row.recoveries,
        ebitdaA: row.financials.ebitdaA, upliftA, annualisedUpliftA: upliftA * daysPerYear,
        netVsBalancedA: cents(row.financials.ebitdaA - balanced.financials.ebitdaA) };
    }),
  ];
  // Seed display order, with the optional ZnSO4 row inside the Pb/Ag group.
  const definitions = new Map(STRATEGY_LEVERS.flatMap(strategy => strategy.levers).map(lever => [lever.id, lever]));
  const rows: CapacityStrategyMatrix['rows'][number][] = [...definitions.values()]
    .sort((a, b) => ['shared', 'lead-silver', 'zinc'].indexOf(a.group) - ['shared', 'lead-silver', 'zinc'].indexOf(b.group))
    .map(lever => ({ id: lever.id, group: lever.group, label: lever.label, driver: lever.driver,
      cells: { current: { setpoint: lever.id === 'feed-rate' ? `${currentFeed.toFixed(1)} stph` : lever.currentValue } } }));
  for (const strategy of strategies) {
    let cumulativeA = 0;
    for (const lever of strategy.levers) {
      const previousCents = cents(cumulativeA);
      cumulativeA += lever.impactA;
      rows.find(row => row.id === lever.id)!.cells[strategy.id] = {
        setpoint: lever.recommendedValue, impactA: cents(cents(cumulativeA) - previousCents),
      };
    }
    // A strategy that leaves a lever alone keeps its current setpoint; no US$ attribution.
    for (const row of rows) row.cells[strategy.id] ??= { setpoint: row.cells.current!.setpoint };
  }
  rows.push({ id: 'zinc-in-lead', group: 'quality', label: 'Zn in Pb concentrate',
    driver: `${balanced.quality.leadConcentrateZnThresholdPct.toFixed(1)}% Zn limit`,
    cells: { current: { setpoint: `${(plantState.zincInLeadConcentratePct ?? DEFAULT_CAPACITY_PLANT_STATE.zincInLeadConcentratePct ?? 0).toFixed(1)}%` },
      ...Object.fromEntries(strategies.map(row => [row.id, { setpoint: `${row.quality.zincInLeadConcentratePct.toFixed(1)}%` }])) } },
  { id: 'zinc-concentrate-grade', group: 'quality', label: 'Zn concentrate grade',
    driver: `${balanced.quality.zincConcentrateGradeMinimumPct.toFixed(1)}% minimum`,
    cells: { current: { setpoint: `${(plantState.concentrateGrades ?? DEFAULT_CAPACITY_PLANT_STATE.concentrateGrades!).zincPct.toFixed(1)}%` },
      ...Object.fromEntries(strategies.map(row => [row.id, { setpoint: `${row.quality.zincConcentrateGradePct.toFixed(1)}%` }])) } });
  return { columns, rows };
}

function ebitdaAt(
  throughputStph: number,
  grade: OreGradeState,
  recoveries: RecoveryState,
  seed: CapacitySeed,
  prices: MetalPriceState,
  plantState: CapacityPlantState,
) {
  return calculateEbitda(throughputStph, grade, recoveries, seed, prices, plantState);
}

function strategyRecoveriesAt(
  id: CapacityStrategyId,
  grade: OreGradeState,
  seed: CapacitySeed,
  recoveryAnchor: RecoveryState,
  plantState: CapacityPlantState,
): CapacityRecoveryProjection {
  const strategy = STRATEGY_LEVERS.find((row) => row.id === id)!;
  const feedStph = capacityOperatingFeed(strategy.feedStph * (plantState.feedStph / seed.currentThroughputStph), { grade, seed, recoveryAnchor, plantState });
  const curve = cappedRecoveriesAtThroughput(feedStph, seed, false, grade, recoveryAnchor, plantState);
  return {
    recoveries: boundedRecovery(Object.fromEntries(RECOVERY_KEYS.map((key) => [
      key,
      curve[key] + strategy.recoveryUplift[key],
    ])) as RecoveryState),
    // The strategy's own recovery is not capped, so it never reads as capped.
    recoveryCapped: false,
    cappedRecoveryMetals: [],
  };
}

function outcomeAtPrice(
  id: CapacityStrategyId,
  silverPerOz: number,
  grade: OreGradeState,
  prices: MetalPriceState,
  seed: CapacitySeed,
  recoveryAnchor: RecoveryState,
  plantState: CapacityPlantState,
): number {
  const strategy = STRATEGY_LEVERS.find((row) => row.id === id)!;
  const feedStph = capacityOperatingFeed(strategy.feedStph * (plantState.feedStph / seed.currentThroughputStph), { grade, seed, recoveryAnchor, plantState });
  return ebitdaAt(
    feedStph,
    grade,
    strategyRecoveriesAt(id, grade, seed, recoveryAnchor, plantState).recoveries,
    seed,
    { ...prices, silverPerOz },
    plantState,
  ).ebitdaA;
}

function findAgBreakeven(
  grade: OreGradeState,
  prices: MetalPriceState,
  seed: CapacitySeed,
  recoveryAnchor: RecoveryState,
  plantState: CapacityPlantState,
): number | null {
  const { silverMin, silverMax } = STRATEGY_PRICE_LIMITS;
  const differenceAt = (price: number) =>
    outcomeAtPrice('lead-silver-max', price, grade, prices, seed, recoveryAnchor, plantState) -
    outcomeAtPrice('balanced', price, grade, prices, seed, recoveryAnchor, plantState);
  const lowDifference = differenceAt(silverMin);
  const highDifference = differenceAt(silverMax);
  if (lowDifference >= 0) return silverMin;
  if (highDifference <= 0) return null;

  let low: number = silverMin;
  let high: number = silverMax;
  for (let iteration = 0; iteration < 48; iteration += 1) {
    const middle = (low + high) / 2;
    if (differenceAt(middle) > 0) high = middle;
    else low = middle;
  }
  return (low + high) / 2;
}

export function computeCapacityStrategies({
  grade,
  prices,
  seed = CAPACITY_SEED,
  recoveryAnchor,
  plantState = DEFAULT_CAPACITY_PLANT_STATE,
}: {
  grade: OreGradeState;
  prices: MetalPriceState;
  seed?: CapacitySeed;
  plantState?: CapacityPlantState;
  recoveryAnchor?: RecoveryState;
}): CapacityStrategyRead {
  const anchor: RecoveryState = recoveryAnchor ?? {
    ...plantState.recoveries,
  };
  const scale = plantState.feedStph / seed.currentThroughputStph;
  const startThroughput = capacityOperatingFeed(plantState.feedStph, { grade, seed, recoveryAnchor: anchor, plantState });
  const startRecoveries = cappedRecoveriesAtThroughput(startThroughput, seed, false, grade, anchor, plantState);
  const currentFinancials = ebitdaAt(startThroughput, grade, startRecoveries, seed, prices, plantState);
  const baseNsrA = currentFinancials.totalNsrA;
  const leadOnePoint = ebitdaAt(
    startThroughput,
    grade,
    {
      ...startRecoveries,
      pbPct: startRecoveries.pbPct + 1,
      agPct: startRecoveries.agPct + 1,
    },
    seed,
    prices,
    plantState,
  ).totalNsrA - baseNsrA;
  const silverOnePoint = ebitdaAt(
    startThroughput,
    grade,
    { ...startRecoveries, agPct: startRecoveries.agPct + 1 },
    seed,
    prices,
    plantState,
  ).totalNsrA - baseNsrA;
  const zincOnePoint = ebitdaAt(
    startThroughput,
    grade,
    { ...startRecoveries, znPct: startRecoveries.znPct + 1 },
    seed,
    prices,
    plantState,
  ).totalNsrA - baseNsrA;

  const strategies = STRATEGY_LEVERS.map((definition) => {
    const strategyFeedStph = capacityOperatingFeed(definition.feedStph * scale, { grade, seed, recoveryAnchor: anchor, plantState });
    let throughputStph = startThroughput;
    let stagedRecoveries = { ...startRecoveries };
    let previousEbitdaA = currentFinancials.ebitdaA;
    const levers: StrategyLeverRead[] = definition.levers.map((lever) => {
      if (lever.id === 'feed-rate') {
        throughputStph = strategyFeedStph;
        stagedRecoveries = cappedRecoveriesAtThroughput(throughputStph, seed, false, grade, anchor, plantState);
      }
      for (const key of RECOVERY_KEYS) {
        const share = lever.recoveryDeltaShare?.[key] ?? 0;
        stagedRecoveries[key] += definition.recoveryUplift[key] * share;
      }
      const nextFinancials = ebitdaAt(
        throughputStph,
        grade,
        boundedRecovery(stagedRecoveries),
        seed,
        prices,
        plantState,
      );
      const impactA = nextFinancials.ebitdaA - previousEbitdaA;
      previousEbitdaA = nextFinancials.ebitdaA;
      return lever.id === 'feed-rate'
        ? {
          ...lever,
          currentValue: `${startThroughput.toFixed(1)} stph`,
          recommendedValue: `${strategyFeedStph.toFixed(1)} stph`,
          driver: `Current WIT-021 feed against ${strategyFeedStph.toFixed(1)} stph strategy feed`,
          impactA,
        }
        : { ...lever, impactA };
    });
    const financials = ebitdaAt(
      strategyFeedStph,
      grade,
      strategyRecoveriesAt(definition.id, grade, seed, anchor, plantState).recoveries,
      seed,
      prices,
      plantState,
    );
    return {
      id: definition.id,
      name: definition.name,
      feedStph: strategyFeedStph,
      quality: definition.quality,
      ...strategyRecoveriesAt(definition.id, grade, seed, anchor, plantState),
      financials,
      levers,
    };
  });

  const best = strategies.reduce((winner, row) =>
    row.financials.ebitdaA > winner.financials.ebitdaA ? row : winner,
  );

  return {
    prices,
    priceDate: BUNKER_HILL_SMELTER_SCHEDULE.prices.priceDate,
    matrix: strategyMatrix(strategies, currentFinancials.ebitdaA, startThroughput, startRecoveries, plantState, seed.daysPerYear),
    daysPerYear: seed.daysPerYear,
    currentFinancials,
    currentEbitdaA: currentFinancials.ebitdaA,
    perPpt: {
      leadSilverA: leadOnePoint,
      zincA: zincOnePoint,
      silverSharePct: leadOnePoint > 0 ? (silverOnePoint / leadOnePoint) * 100 : 0,
    },
    strategies,
    recommendedId: best.id,
    breakevenAgOz: findAgBreakeven(grade, prices, seed, anchor, plantState),
  };
}
